/**
 * This component is the collected Vendor Application.
 */
import {
  assign,
  forEach,
  get,
  includes,
  isEmpty,
  keyBy,
  keys,
  mapValues,
  omit,
  omitBy,
  pick,
  size,
  startCase,
  trim,
} from 'lodash';
import moment from 'moment';
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import FormControl from '@mui/material/FormControl';
import Input from '@mui/material/Input';
import InputLabel from '@mui/material/InputLabel';

import { Prompt } from 'react-router-dom';
import {
  Clickable,
  Tabs,
  HideText,
  Icon,
} from '@fashionnovateam/nova-ui-toolkit';
import styles from './VendorApplication.module.css';
import matchProps from '../../Lib/Utils/PropTypes/match-props';
import locationProps from '../../Lib/Utils/PropTypes/location-props';
import AddressFields from '../AddressFields/AddressFields';

import {
  makeAddressNames,
  makeAddressValues,
} from '../../Lib/Utils/FormUtils/address-fields';

import {
  makeBankNames,
  makeBankValues,
} from '../../Lib/Utils/FormUtils/bank-fields';

import {
  buildContactEmail,
  findMissingFields,
  getApplicationStatus,
  isFieldDisabled,
  isFieldBankingRequestEnabled,
  isFileFieldDisabled,
  isValidPhone,
  isValidEmail,
  makeSubmitButtonText,
  showSubmitButton,
  validateFormForCreate,
  validateFormForUpdate,
} from '../../Lib/Utils/FormUtils/vendor-application';

import ApplicationStatus from '../../Lib/Constants/vendor-application-status';
import VendorFileFields from '../../Lib/Constants/vendor-file-fields';
import FileInput from '../FileInput/FileInput';
import BankFields from '../BankFields/BankFields';
import WireBankFields from '../WireBankFields/WireBankFields';
import VendorAttributionForm from '../VendorAttribution/VendorAttributionForm';
import TextInput from '../ReusableComponents/TextInput/TextInput';
import SelectInput from '../ReusableComponents/SelectInput/SelectInput';
import { VendorTaxClassifications } from '../../Lib/Constants/tax-classifications';
import { ReactComponent as Download } from '../../Assets/icons/download.svg';
import Button from '../ReusableComponents/Button/Button';
import PrintApplicationButton from './PrintApplicationButton';
import SubmitApplicationChecksModal from '../ReusableComponents/Modals/SubmitApplicationChecksModal/SubmitApplicationChecksModal';
import ComplianceSection from './ComplianceSection';
import maskTaxId from '../../Lib/Utils/VendorApplication/mask-tax-id';
import isDomestic from '../../Lib/Utils/VendorApplication/is-domestic';
import vendorTypes from '../../Lib/Constants/invite-token-vendor-types';
import Role, { Actions, hasPermissionTo } from '../../Lib/Constants/roles.ts';
import ButtonSwitch from '../VendorAttribution/ButtonSwitch';
import BankFieldsUpdate from '../BankFieldsUpdate/BankFieldsUpdate';
import Page from '../Page/index.ts';
import Header from '../Header/index.ts';

/**
 * Creates a tax classification input to diaplay the value from netsuite.
 *
 *  @param {object} form the vendor application data
 */
const makeTaxClassificationSelectInput = (form) => (
  <Input
    name="tax_classification"
    id="va-tax-classification"
    value={form.tax_classification || ''}
  />
);

/**
 *  Sets up the values of the form to be passed to state. Populates fields
 * already completed by the user or that have already been saved in the db.
 *
 *  @param {object} vendor the vendor object returned to the Vendor Application
 *
 *  @returns {object} form data from the vendor record in the db.
 */
const setupForm = (vendor) => {
  const form = omit(vendor, [
    'entity_id',
    'internal_id',
    'bank_account_type',
    'rejection_reason',
    'type',
    'payment_file_format',
    'application_status',
    'application_archived_at',
    'is_children_clothing',
    'factory_compliance_report',
    'fcr_expiration',
    'third_party_garment',
    'custentity_fn_mh_prop65_test',
    'banking_request_rejection_reason',
    'banking_request_status',
    'top_sample_acknowledgement',
    'printing_onboarding',
    'init_counter_email_sent_at',
    'domestic_merch_po_eligible',
    'inactive',
  ]);

  const files = pick(vendor, keys(VendorFileFields));

  forEach(files, (_, key) => {
    form[key] = files[key].exists;
  });

  return form;
};

/**
 * Maps out the read links for the file downloads.
 *
 *  @param {object} vendor the vendor object returned to the Vendor Application
 *
 *  @returns {object} an object with the file names as keys, and the read urls
 *     as values.
 */
const setupFileLinks = (vendor) => {
  const files = pick(vendor, keys(VendorFileFields));
  return mapValues(files, 'read');
};

function VendorApplication(props) {
  const [formState, setFormState] = useState(setupForm(props.vendor));
  const [savedState, setSavedState] = useState(false);
  const [tabState, setTabState] = useState(0);
  const [wirePaymentState, setWirePaymentState] = useState(false);
  const [sameAddressState, setSameAddressState] = useState(false);
  const [showSubmitChecksState, setShowSubmitChecksState] = useState(false);
  const [showBankFieldsUpdateState, setShowBankFieldsUpdateState] =
    useState(false);
  const [updateState, setUpdateState] = useState({});
  const [emptyFieldsState, setEmptyFieldsState] = useState({});

  const perms = useMemo(() => {
    const canRead = hasPermissionTo(
      'users/:id/profile',
      props.currentUser.role,
      Actions.READ,
      'PROFILE',
    );
    const canCreate =
      hasPermissionTo(
        'users/:id/profile',
        props.currentUser.role,
        Actions.CREATE,
        'PROFILE',
      ) && !props.vendor?.application_archived_at;
    const canUpdate =
      hasPermissionTo(
        'users/:id/profile',
        props.currentUser.role,
        Actions.UPDATE,
        'PROFILE',
      ) && !props.vendor?.application_archived_at;
    const canDelete =
      hasPermissionTo(
        'users/:id/profile',
        props.currentUser.role,
        Actions.DELETE,
        'PROFILE',
      ) && !props.vendor?.application_archived_at;
    return {
      canRead,
      canCreate,
      canUpdate,
      canDelete,
    };
  }, [props.currentUser, props.vendor?.application_archived_at]);

  const uploadedFileLinks = setupFileLinks(props.vendor);

  const {
    match: {
      params: { id: idOriginallyFromState },
    },
  } = props;

  const vendorFromNewVendorOnboarding =
    props.vendor &&
    props.vendor.applications &&
    props.vendor.applications.length > 0;

  useEffect(() => {
    function onLeavePage(e) {
      const msg =
        'You have not submitted the application, are you sure you want to leave?';

      if (!savedState && Object.keys(updateState).length > 0) {
        e.returnValue = msg;
        return msg;
      }
      if (get(props.vendor, 'submitted_at')) return undefined;

      const status = getApplicationStatus(props.vendor);
      const unsubmittedStatus = includes(
        [ApplicationStatus.APPLICATION_PENDING, ApplicationStatus.REJECTED],
        status,
      );

      if (!unsubmittedStatus) return undefined;

      e.returnValue = msg;
      return msg;
    }

    window.addEventListener('beforeunload', onLeavePage);

    // return a function to execute at unmount
    return () => {
      window.removeEventListener('beforeunload', onLeavePage);
    };
  }, []);

  useEffect(() => {
    const form = { ...formState };
    if (isDomestic(form.payment_country) && form.coi_not_available) {
      toggleNoCoi();
    }
  }, [formState]);

  /**
   * When moving focus off of an input, applies validation to fields in real
   * time via onBlur function in props
   * @param {object} target: {
   *  @property {string} name: the name of the input being blurred,
   *  @property {string} value: the value of the input being blurred
   *    }
   */
  const onBlur = ({ target: { name, value } }) => {
    const trimmedValue = trim(value);
    const emptyFields = { ...emptyFieldsState };
    updateInputField({ target: { name, value: trimmedValue } });
    if (value && !emptyFields[name]) {
      delete emptyFields[name];
    } else if (
      findMissingFields({ ...formState }, false, isUpdate()).includes(name)
    ) {
      assign(emptyFields, { [name]: isEmpty(trimmedValue) });
    }
    setEmptyFieldsState(emptyFields);
  };

  /**
   * Opens a window with the product guidelines document for the user to download.
   */
  const showProductGuidelines = () => {
    const { publicFilesUrls } = props;
    window.open(get(publicFilesUrls, 'routing_and_product_guidelines.read'));
  };

  /**
   * Opens a window with the product guidelines document for the user to download.
   */
  const showRoutingIndex = () => {
    const { publicFilesUrls } = props;
    window.open(get(publicFilesUrls, 'routing_vendor_index.read'));
  };

  /**
   * Checks to see if the application has been updated.
   */
  const isUpdate = () => {
    const { vendor } = props;
    const applicationStatus = getApplicationStatus(vendor);
    return (
      applicationStatus === ApplicationStatus.APPROVED ||
      (applicationStatus === ApplicationStatus.REJECTED &&
        vendor.internal_id !== null)
    );
  };

  /**
   * Syncs the values from the main address, and copies them to the payment
   * address fields.
   */
  const syncAddressFields = (form) => {
    const id = idOriginallyFromState;
    const { updateVendor } = props;
    const address = makeAddressValues(form);
    const addressKeys = makeAddressNames();
    const payAddress = makeAddressNames(true);
    const update = {};

    forEach(payAddress, (val, key) => {
      const value = address[addressKeys[key]];
      assign(form, { [val]: value });
      update[val] = value;
    });

    if (!isUpdate()) {
      setFormState(form);
      updateVendor(id, update);
    } else {
      setUpdateState((prevState) => ({
        ...prevState,
        ...update,
      }));
    }
  };

  /**
   * Updates the address data in the form, checks the old form data vs the new
   * changes, and updates the vendor record.
   * @param {string} name the name of the input being updated
   * @param {string} value the value of the updated input
   */
  const updateAddressField = (name, value) => {
    const id = idOriginallyFromState;
    const oldForm = { ...formState };
    const { updateVendor } = props;
    const updates = { [name]: trim(value) };
    const form = assign(oldForm, updates);

    setSavedState(false);

    setFormState(form);
    if (sameAddressState) {
      syncAddressFields(form);
    } else if (!isUpdate()) {
      updateVendor(id, updates);
    } else {
      setUpdateState((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  /**
   * Updates the bank data in the form, checks the old form data vs the new
   * changes, and updates the vendor record.
   * @param {string} name the name of the input being updated
   * @param {string} value the value of the updated input
   */
  const updateBankField = (name, value) => {
    const id = idOriginallyFromState;
    const oldForm = { ...formState };
    const { updateVendor } = props;

    const spaceEnabledFields = ['account_name', 'bank_name'];

    const updates = {
      [name]: spaceEnabledFields.includes(name) ? value : value.trim(),
    };

    const form = assign(oldForm, updates);

    if (isUpdate()) {
      setUpdateState((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    } else {
      updateVendor(id, updates);
    }
    setFormState(form);
    setSavedState(false);
  };

  /**
   * Updates the input data in the form, checks the old form data vs the new
   * changes, and updates the vendor record.
   * @param {string} name the name of the input being updated
   * @param {string} value the value of the updated input
   */
  const updateInputField = ({ target: { name, value } }) => {
    const id = idOriginallyFromState;
    const form = { ...formState };
    const { updateVendor } = props;
    const formData = { [name]: value };

    if (name === 'email' || name === 'remittance_advice_email') {
      if (isValidEmail(value)) {
        setEmptyFieldsState((prev) => ({ ...prev, [name]: true }));
        const updatesInvalid = assign(form, formData);
        setFormState(updatesInvalid);
        setSavedState(false);
        return;
      }
      // clear error if previously set
      setEmptyFieldsState((prev) => {
        if (!prev[name]) return prev;
        const { [name]: _omitted, ...rest } = prev;
        return rest;
      });
    }
    const updates = assign(form, formData);

    if (isUpdate()) {
      setUpdateState((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    } else {
      updateVendor(id, formData);
    }
    setFormState(updates);
    setSavedState(false);
  };

  const updateVendorTypeField = (value) => {
    const id = idOriginallyFromState;
    const form = { ...formState };
    const { updateVendor } = props;

    // Only clear fields for the non-selected type
    const formData = {
      is_individual: value,
    };

    if (value) {
      // Individual selected - clear company fields only
      formData.company_name = null;
      formData.legal_name = null;
    } else {
      // Company selected - clear individual fields only
      formData.first_name = null;
      formData.middle_name = null;
      formData.last_name = null;
    }

    const updates = assign(form, formData);

    if (isUpdate()) {
      setUpdateState((prevState) => ({
        ...prevState,
        ...formData,
      }));
    } else {
      updateVendor(id, formData);
    }
    setFormState(updates);
    setSavedState(false);
  };

  /**
   * Updates the date input data in the form, checks the old form data vs the new
   * changes, and updates the vendor record.
   * @param {string} name the name of the input being updated
   * @param {string} value the value of the updated input
   */
  const updateDateInputField = ({ target: { name, value } }) => {
    const id = idOriginallyFromState;
    const form = { ...formState };
    const { updateVendor } = props;
    const formData = { [name]: isEmpty(value) ? null : value };
    const updates = assign(form, formData);

    if (isUpdate()) {
      setUpdateState((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    } else {
      updateVendor(id, formData);
    }
    setFormState(updates);
    setSavedState(false);
  };

  /**
   * When moving focus off of a date input, applies validation to fields in real
   * time via onBlur function in props
   * @param {object} target: {
   *  @property {string} name: the name of the input being blurred,
   *  @property {string} value: the value of the input being blurred
   *    }
   */
  const handleCoiExpirationDateChange = ({ target: { name, value } }) => {
    if (moment(value, 'YYYY-MM-DD') < moment()) {
      updateDateInputField({
        target: {
          name,
          value: moment().format('YYYY-MM-DD').valueOf(),
        },
      });
    }
  };

  /**
   * Handles the committing of file information to the database when uploading
   * files on the form. Updates the vendor record
   * @param {string} name the name of the input being updated
   * @param {object} file file being uploaded
   */
  const updateFileField = async (name, file) => {
    const id = idOriginallyFromState;
    const form = { ...formState };
    const { updateVendor } = props;
    const {
      vendor: { id: vendorId },
    } = props;
    const formData = { [name]: file, id: vendorId };
    const updatedForm = assign(form, { [name]: true });

    setFormState(updatedForm);
    updateVendor(id, formData, true);
    setSavedState(false);
  };

  /**
   * Checks that the form is complete and whether it has been submitted or
   * needs to be saved for later completion.
   * @param {object} e the event passed back when the submit button is clicked
   * on the form.
   */
  const submitOrSaveVendor = (e) => {
    if (e) e.preventDefault();

    if (Object.keys(updateState).length === 0 && isUpdate(props.vendor))
      return false;

    const hasWirePayment = false;
    const form = { ...formState };
    const trimmedForm = mapValues(form, (value) =>
      typeof value === 'string' ? trim(value) : value,
    );
    setFormState(trimmedForm);

    const validForm = isUpdate()
      ? validateFormForUpdate(trimmedForm)
      : validateFormForCreate(trimmedForm, hasWirePayment);

    const emailFields = ['email', 'remittance_advice_email'];
    const hasEmailErrors = emailFields.some((field) => {
      const value = trimmedForm[field];
      return !value || isValidEmail(value);
    });

    if (!validForm || hasEmailErrors) {
      const emptyFields = mapValues(
        keyBy(findMissingFields(trimmedForm, hasWirePayment, isUpdate())),
        () => true,
      );

      if (hasEmailErrors) {
        emailFields.forEach((field) => {
          const value = trimmedForm[field];
          if (!value || isValidEmail(value)) {
            emptyFields[field] = true;
          }
        });
      }

      return setEmptyFieldsState(emptyFields);
    }

    if (props.vendor.internal_id || props.vendor.approved_at) {
      setSavedState(true);

      return props
        .updateVendorNetSuite(props.user.id, updateState)
        .then(() => {
          setUpdateState({});
          setEmptyFieldsState({});
        })
        .catch(console.log);
    }

    return setShowSubmitChecksState(true);
  };

  /**
   * Toggles the fields to display domestic or international inputs on the
   * vendor application.
   * @param {object} e the event passed when the toggle button is clicked
   */
  const toggleWire = (e) => {
    if (e) e.preventDefault();

    setWirePaymentState((prevWirePayment) => !prevWirePayment);
  };

  /**
   * Toggles the form to use the same address for both sets of address fields
   * on the Application. Calls syncAddressFields.
   */
  const toggleSameAddress = () => {
    setSameAddressState((prevSameAddressState) => !prevSameAddressState);
    const form = { ...formState };
    syncAddressFields(form);
  };

  /**
   * Toggles the coi upload for international vendors
   * Toggle is disabled when coi field has data
   * When toggle is true, coi field is disabled
   */
  const toggleNoCoi = () => {
    const id = idOriginallyFromState;
    const form = { ...formState };
    const { updateVendor } = props;
    const formData = { coi_not_available: !form.coi_not_available };
    if (formData.coi_not_available) {
      formData.coi_expiration_date = null;
    }
    const updates = assign(form, formData);

    setFormState(updates);
    updateVendor(id, formData);
    setSavedState(false);
  };

  const isNoCoiDisabled = () =>
    !sameAddressState ? !domesticPayment : !domesticCompany;

  const handleTab = (newValue) => setTabState(newValue);

  const {
    vendor,
    currentUser,
    user,
    publicFilesUrls,
    updateVendorAttributes,
    vendorAttributesError = {},
    location,
  } = props;
  const { rejection_reason, internal_id } = vendor;
  const submittedAt = get(vendor, 'submitted_at', formState.submitted_at);
  const domesticPayment = isDomestic(formState.payment_country);
  const domesticCompany = isDomestic(formState.country);
  const applicationStatus = getApplicationStatus(vendor);
  const inviteToken = get(vendor, 'invite_token', {});
  const isBeauty =
    get(inviteToken, 'vendor_type') === vendorTypes.BEAUTY_VENDOR;
  const isCompliance = includes(
    [Role.COMPLIANCE, Role.ADMIN],
    get(currentUser, 'role'),
  );

  // TODO: [HALO-MIGRATION] Remove isMigratedToHalo and all its usages after migration is complete
  const isMigratedToHalo =
    currentUser?.migrated_to_halo === 'influencer' ||
    currentUser?.migrated_to_halo === 'agency';

  if (isEmpty(publicFilesUrls)) return null;
  if (!vendor || isEmpty(vendor)) return null;
  if (!user || isEmpty(user)) return null;

  return (
    <Page.Container withoutTwp>
      <Header.Major icon="briefcase-job" title="Profile">
        <PrintApplicationButton vendor={vendor} currentUser={currentUser} />
      </Header.Major>
      <Header.Minor>
        <Tabs.Group type="segment" onChange={handleTab}>
          <Clickable onClick={() => handleTab(0)}>
            <Tabs.Item
              type="segment"
              icon="user/user-user-01"
              isActive={tabState === 0}
            >
              <HideText notAbove="lg">Profile</HideText>
            </Tabs.Item>
          </Clickable>
          <Clickable onClick={() => handleTab(1)}>
            <Tabs.Item type="segment" icon="notebook" isActive={tabState === 1}>
              <HideText notAbove="lg">Attributes</HideText>
            </Tabs.Item>
          </Clickable>
        </Tabs.Group>
      </Header.Minor>
      <Page.Content>
        {tabState === 0 && (
          <form className={styles.container} onSubmit={submitOrSaveVendor}>
            {showSubmitChecksState && (
              <SubmitApplicationChecksModal
                form={formState}
                onClose={() => setShowSubmitChecksState(false)}
                open={showSubmitChecksState}
                submitVendor={
                  vendorFromNewVendorOnboarding
                    ? props.createVendorNetSuite
                    : props.submitVendor
                }
                user={props.user}
                validateBIC={props.validateBIC}
                validateIBAN={props.validateIBAN}
                validateTIN={props.validateTIN}
              />
            )}

            <div className={styles.mainHeader}>
              <h2 className={styles.profileHeader}>Vendor Profile</h2>
            </div>

            {process.env.NODE_ENV !== 'development' && (
              <Prompt
                when={
                  submittedAt &&
                  !savedState &&
                  Object.keys(updateState).length > 0
                }
                message="Your changes have not been saved, do you want to quit anyway?"
              />
            )}
            <div className={styles.statusRow}>
              <h5>
                <span>Vendor Status: </span>
                <span className={styles.applicationStatus}>
                  {startCase(applicationStatus)}
                </span>
              </h5>
            </div>
            {!internal_id && !rejection_reason && (
              <span>
                <p className={styles.vendorSupportContact}>
                  {`If you are an existing Vendor with Fashion Nova and the status
                above shows APPLICATION PENDING, please contact `}
                  <a href={`${buildContactEmail(currentUser)}`}>
                    {currentUser.role === Role.INFLUENCER
                      ? 'influencerdocs@fashionnova.com'
                      : 'vendormasterdata@fashionnova.com'}
                  </a>
                  {` to gain access to your account`}
                </p>
              </span>
            )}

            {rejection_reason &&
              applicationStatus === ApplicationStatus.REJECTED && (
                <FormControl variant="standard" margin="dense" fullWidth>
                  <div
                    id="va-rejection-reason"
                    className={styles.rejectionReason}
                  >
                    {`Rejection Reason - ${startCase(rejection_reason)}`}
                  </div>
                </FormControl>
              )}
            <br />

            <div className={styles.row}>
              <div>
                <InputLabel htmlFor="influencer-individual-or-company">
                  Are you applying as an Individual or a Company?
                </InputLabel>
                <ButtonSwitch
                  selected={!!formState.is_individual}
                  left="individual"
                  leftLabel="Individual"
                  right="company"
                  rightLabel="Company"
                  id="va-individual-or-company"
                  onChange={updateVendorTypeField}
                  disabled={
                    isFieldDisabled(applicationStatus) ||
                    !perms.canUpdate ||
                    isMigratedToHalo
                  }
                />
              </div>
            </div>

            {!formState.is_individual && (
              <div className={styles.row}>
                <div className={styles.halfRow}>
                  <TextInput
                    fullWidth
                    required
                    error={!!emptyFieldsState.legal_name}
                    label="Registered Legal Name"
                    variant="outlined"
                    onChange={updateInputField}
                    onBlur={onBlur}
                    name="legal_name"
                    value={formState.legal_name || ''}
                    id="va-legal-name"
                    autoComplete="no"
                    disabled={
                      isFieldDisabled(applicationStatus) ||
                      !perms.canUpdate ||
                      isMigratedToHalo
                    }
                  />
                </div>

                <div className={styles.halfRow}>
                  <TextInput
                    fullWidth
                    required
                    error={!!emptyFieldsState.company_name}
                    label="Company Name"
                    variant="outlined"
                    onChange={updateInputField}
                    onBlur={onBlur}
                    name="company_name"
                    value={formState.company_name || ''}
                    id="va-company-name"
                    autoComplete="no"
                    disabled={
                      isFieldDisabled(applicationStatus) ||
                      !perms.canUpdate ||
                      isMigratedToHalo
                    }
                  />
                </div>
              </div>
            )}

            {formState.is_individual && (
              <div className={styles.row}>
                <TextInput
                  fullWidth
                  required
                  error={!!emptyFieldsState.first_name}
                  label="First Name"
                  variant="outlined"
                  onChange={updateInputField}
                  onBlur={onBlur}
                  name="first_name"
                  value={formState.first_name || ''}
                  id="va-first-name"
                  autoComplete="no"
                  disabled={!perms.canUpdate || isMigratedToHalo}
                />
                <TextInput
                  fullWidth
                  label="Middle Name"
                  variant="outlined"
                  onChange={updateInputField}
                  onBlur={onBlur}
                  name="middle_name"
                  value={formState.middle_name || ''}
                  id="va-middle-name"
                  autoComplete="no"
                  disabled={!perms.canUpdate || isMigratedToHalo}
                />
                <TextInput
                  fullWidth
                  required
                  error={!!emptyFieldsState.last_name}
                  label="Last Name"
                  variant="outlined"
                  onChange={updateInputField}
                  onBlur={onBlur}
                  name="last_name"
                  value={formState.last_name || ''}
                  id="va-last-name"
                  autoComplete="no"
                  disabled={!perms.canUpdate || isMigratedToHalo}
                />
              </div>
            )}

            <AddressFields
              values={makeAddressValues(formState)}
              names={makeAddressNames()}
              domestic={domesticCompany}
              prefix="va-company"
              onChange={updateAddressField}
              onBlur={onBlur}
              emptyFields={emptyFieldsState}
              disabled={
                applicationStatus === ApplicationStatus.PENDING_APPROVAL ||
                !perms.canUpdate ||
                isMigratedToHalo
              }
              required
              isVendorApplication
            />

            <br />

            <h4 className={styles.subHeader}>COMPANY CONTACT INFO</h4>
            <div className={styles.row}>
              <div className={styles.halfRow}>
                <TextInput
                  fullWidth
                  required
                  error={isValidPhone(
                    formState.phone,
                    !!emptyFieldsState.phone,
                  )}
                  label="Phone Number"
                  variant="outlined"
                  onChange={updateInputField}
                  onBlur={onBlur}
                  name="phone"
                  value={formState.phone || ''}
                  id="va-phone"
                  autoComplete="no"
                  disabled={
                    isFieldDisabled(applicationStatus) ||
                    !perms.canUpdate ||
                    isMigratedToHalo
                  }
                />
              </div>
              <div className={styles.halfRow}>
                <TextInput
                  fullWidth
                  required
                  error={
                    !!emptyFieldsState.email || isValidEmail(formState.email)
                  }
                  label="Email"
                  variant="outlined"
                  onChange={updateInputField}
                  onBlur={onBlur}
                  name="email"
                  value={formState.email || ''}
                  id="va-email"
                  autoComplete="no"
                  disabled={
                    isFieldDisabled(applicationStatus) ||
                    !perms.canUpdate ||
                    isMigratedToHalo
                  }
                />
              </div>
              <div className={styles.halfRow}>
                <TextInput
                  fullWidth
                  required
                  error={
                    !!emptyFieldsState.remittance_advice_email ||
                    isValidEmail(formState.remittance_advice_email)
                  }
                  label="Remittance Email"
                  variant="outlined"
                  onChange={updateInputField}
                  onBlur={onBlur}
                  name="remittance_advice_email"
                  value={formState.remittance_advice_email || ''}
                  id="va-email-remittance"
                  autoComplete="no"
                  disabled={
                    isFieldDisabled(applicationStatus) ||
                    !perms.canUpdate ||
                    isMigratedToHalo
                  }
                />
              </div>
            </div>
            <div className="mt-2 ml-[66.66%] flex items-center text-[#666] text-sm w-1/3">
              <Icon.InformationCircle className="mr-2 text-neutral-400 text-sm flex-shrink-0" />
              <span>
                Email address where we&apos;ll send payment confirmations and
                details.
              </span>
            </div>

            <h4 className={styles.header}>Payment Information</h4>
            <h4>
              <span className={styles.subHeader}>PAYMENT ADDRESS</span>
              {!submittedAt && !internal_id && (
                <label
                  className={styles.sameAddress}
                  htmlFor="same-address-checkbox"
                >
                  <input
                    id="same-address-checkbox"
                    type="checkbox"
                    checked={sameAddressState}
                    onChange={toggleSameAddress}
                    disabled={!perms.canUpdate || isMigratedToHalo}
                  />
                  same as company address
                </label>
              )}
            </h4>

            {!sameAddressState && (
              <AddressFields
                values={makeAddressValues(formState, true)}
                names={makeAddressNames(true)}
                prefix="va-payment"
                domestic={domesticPayment}
                onChange={updateAddressField}
                onBlur={onBlur}
                emptyFields={emptyFieldsState}
                required
                isVendorApplication
                disabled={
                  isFieldDisabled(applicationStatus) ||
                  !perms.canUpdate ||
                  isMigratedToHalo
                }
              />
            )}
            <br />

            <BankFieldsUpdate
              emptyFields={emptyFieldsState}
              open={showBankFieldsUpdateState}
              vendor={props.vendor}
              values={{
                ...pick(formState, [
                  'payment_billing_address',
                  'payment_billing_address_2',
                  'payment_country',
                  'payment_state',
                  'payment_city',
                  'payment_zip',
                ]),
              }}
              names={makeBankNames()}
              onClose={() => setShowBankFieldsUpdateState(false)}
              onSave={(updates) => {
                props.updateVendorBanking(props.user.id, updates).then(() => {
                  props
                    .updateVendorNetSuite(props.user.id, updates)
                    .then(() => {
                      setFormState((prevState) => ({
                        ...prevState,
                        ...updates,
                      }));
                      setSavedState(true);
                      setEmptyFieldsState({});
                      setShowBankFieldsUpdateState(false);
                    });
                });
              }}
            />

            <BankFields
              values={makeBankValues(formState)}
              names={makeBankNames()}
              prefix="va"
              onChange={updateBankField}
              showUpdateButton={
                props.currentUser.role === 'VENDOR' && !isMigratedToHalo
              }
              onUpdateButtonClick={
                isFieldBankingRequestEnabled(applicationStatus)
                  ? () => setShowBankFieldsUpdateState(true)
                  : undefined
              }
              onBlur={onBlur}
              emptyFields={emptyFieldsState}
              disabled={
                isFieldDisabled(applicationStatus) ||
                !perms.canUpdate ||
                isMigratedToHalo
              }
              domestic={domesticPayment}
              header="Payment Info"
              banking_request_status={vendor.banking_request_status}
              required
            />

            <WireBankFields
              perms={perms}
              onBlur={onBlur}
              toggleWire={toggleWire}
              emptyFields={emptyFieldsState}
              updateBankField={updateBankField}
              wirePayment={wirePaymentState}
              vendor={vendor}
              form={formState}
              isMigratedToHalo={isMigratedToHalo}
            />
            <br />

            {domesticCompany && (
              <div className={styles.documentContainer}>
                <div className={styles.row}>
                  <FileInput
                    perms={{
                      canRead: perms.canRead,
                      canUpload: perms.canUpdate && !isMigratedToHalo,
                      canDelete: perms.canDelete && !isMigratedToHalo,
                    }}
                    name="w9_form"
                    header="W9 FORM"
                    suffix="w9"
                    downloadText="W9 Form Download"
                    desc="Please upload a filled out w9 form"
                    disabled={
                      isFileFieldDisabled(applicationStatus) ||
                      !perms.canUpdate ||
                      isMigratedToHalo
                    }
                    defaultFileLink={get(publicFilesUrls, 'w9_form.read')}
                    fileLink={uploadedFileLinks.w9_form}
                    progressEvent={get(
                      publicFilesUrls,
                      `w9_form.progressEvent`,
                    )}
                    exists={!!formState.w9_form}
                    onChange={updateFileField}
                    fileType={['application/pdf']}
                    fullWidth
                    required
                    textFields={
                      <div className={styles.row}>
                        <TextInput
                          fullWidth
                          required
                          error={!!emptyFieldsState.tax_id}
                          label="Tax ID"
                          className={styles.rowItem}
                          variant="outlined"
                          onChange={updateInputField}
                          onBlur={onBlur}
                          name="tax_id"
                          value={
                            maskTaxId(
                              formState.tax_id,
                              isFieldDisabled(applicationStatus),
                            ) || ''
                          }
                          id="va-tax-id"
                          disabled={
                            isFieldDisabled(applicationStatus) ||
                            !perms.canUpdate ||
                            isMigratedToHalo
                          }
                        />

                        <SelectInput
                          fullWidth
                          required
                          error={!!emptyFieldsState.tax_classification}
                          label="Tax Classification"
                          native
                          id="va-tax-classification"
                          value={formState.tax_classification || ''}
                          onChange={updateInputField}
                          onBlur={onBlur}
                          disabled={
                            isFieldDisabled(applicationStatus) ||
                            !perms.canUpdate ||
                            isMigratedToHalo
                          }
                          input={makeTaxClassificationSelectInput(formState)}
                          name="tax_classification"
                          inputProps={{
                            name: 'tax_classification',
                            id: 'tax_classification',
                          }}
                          options={VendorTaxClassifications}
                          className={styles.rowItem}
                        />
                      </div>
                    }
                  />
                </div>
              </div>
            )}

            <div className={styles.documentContainer}>
              {isBeauty ? (
                <FileInput
                  perms={{
                    canRead: perms.canRead,
                    canUpload: perms.canUpdate && !isMigratedToHalo,
                    canDelete: perms.canDelete && !isMigratedToHalo,
                  }}
                  name="beauty_vendor_agreement"
                  desc="Please Download, Sign, and Upload The Agreement."
                  header="BEAUTY AGREEMENT"
                  suffix="beauty-vendor-agreement"
                  disabled={
                    isFileFieldDisabled(applicationStatus) ||
                    !perms.canUpdate ||
                    isMigratedToHalo
                  }
                  defaultFileLink={get(
                    publicFilesUrls,
                    `beauty_vendor_agreement.read`,
                  )}
                  progressEvent={get(
                    publicFilesUrls,
                    `beauty_vendor_agreement.progressEvent`,
                  )}
                  fileLink={uploadedFileLinks.beauty_vendor_agreement}
                  exists={!!formState.beauty_vendor_agreement}
                  onChange={updateFileField}
                  fileType={['application/pdf']}
                  fullWidth
                  required
                />
              ) : (
                <FileInput
                  perms={{
                    canRead: perms.canRead,
                    canUpload: perms.canUpdate && !isMigratedToHalo,
                    canDelete: perms.canDelete && !isMigratedToHalo,
                  }}
                  name="vendor_agreement"
                  desc="Please Download, Sign, and Upload The Agreement."
                  header="VENDOR AGREEMENT"
                  suffix="vendor-agreement"
                  disabled={
                    isFileFieldDisabled(applicationStatus) ||
                    !perms.canUpdate ||
                    isMigratedToHalo
                  }
                  defaultFileLink={get(
                    publicFilesUrls,
                    `vendor_agreement.read`,
                  )}
                  progressEvent={get(
                    publicFilesUrls,
                    `vendor_agreement.progressEvent`,
                  )}
                  fileLink={uploadedFileLinks.vendor_agreement}
                  exists={!!formState.vendor_agreement}
                  onChange={updateFileField}
                  fileType={['application/pdf']}
                  fullWidth
                  required
                />
              )}
            </div>

            <div className={styles.documentContainer}>
              <FileInput
                perms={{
                  canRead: perms.canRead,
                  canUpload: perms.canUpdate && !isMigratedToHalo,
                  canDelete: perms.canDelete && !isMigratedToHalo,
                }}
                name="certificate_of_insurance"
                header="CERTIFICATE OF INSURANCE"
                suffix="certificate-of-insurance"
                desc="Upload a copy of a current General Liability Insurance (COI) with no less than $2 million of coverage per event that should name Fashion Nova as an “additional insured” on “certificate holder”"
                disabled={
                  isFileFieldDisabled(applicationStatus) ||
                  formState.coi_not_available ||
                  isMigratedToHalo
                }
                progressEvent={get(
                  publicFilesUrls,
                  `certificate_of_insurance.progressEvent`,
                )}
                fileLink={uploadedFileLinks.certificate_of_insurance}
                exists={!!formState.certificate_of_insurance}
                onChange={updateFileField}
                fileType={['application/pdf']}
                fullWidth
                textFields={
                  <div>
                    <div className={styles.row}>
                      <FormControl
                        variant="standard"
                        margin="dense"
                        fullWidth
                        className={styles.expirationDate}
                        error={
                          !!formState.certificate_of_insurance &&
                          new Date(formState.coi_expiration_date) <
                            new Date() &&
                          !formState.coi_not_available
                        }
                      >
                        <InputLabel shrink htmlFor="va-coi-expiration-date">
                          Certificate of Insurance Expiration Date
                        </InputLabel>
                        <Input
                          variant="outlined"
                          name="coi_expiration_date"
                          value={
                            formState.coi_expiration_date
                              ? moment
                                  .utc(formState.coi_expiration_date)
                                  .format('YYYY-MM-DD')
                              : ''
                          }
                          inputProps={{
                            min: moment().format('YYYY-MM-DD'),
                          }}
                          id="va-coi-expiration-date"
                          onChange={updateDateInputField}
                          onBlur={handleCoiExpirationDateChange}
                          type="date"
                          disabled={
                            applicationStatus ===
                              ApplicationStatus.PENDING_APPROVAL ||
                            formState.coi_not_available ||
                            !perms.canUpdate ||
                            isMigratedToHalo
                          }
                        />
                      </FormControl>
                    </div>
                    <div className={styles.row}>
                      {isNoCoiDisabled() && (
                        <label
                          className={
                            formState.certificate_of_insurance ||
                            (submittedAt && !internal_id)
                              ? styles.noCoiDisabled
                              : ''
                          }
                          htmlFor="international-no-coi-checkbox"
                        >
                          <input
                            id="international-no-coi-checkbox"
                            type="checkbox"
                            checked={formState.coi_not_available}
                            onChange={toggleNoCoi}
                            disabled={
                              !!formState.certificate_of_insurance ||
                              (!!submittedAt && !internal_id) ||
                              !perms.canUpdate ||
                              isMigratedToHalo
                            }
                          />
                          Not available
                        </label>
                      )}
                    </div>
                  </div>
                }
              />
            </div>

            <div className={styles.documentContainer}>
              <FileInput
                perms={{
                  canRead: perms.canRead,
                  canUpload: perms.canUpdate && !isMigratedToHalo,
                  canDelete: perms.canDelete && !isMigratedToHalo,
                }}
                name="proposition65"
                header="PROPOSITION 65"
                suffix="proposition-65"
                desc="Please Download, Sign, and Submit the Proposition 65 Form."
                disabled={
                  isFileFieldDisabled(applicationStatus) ||
                  !perms.canUpdate ||
                  isMigratedToHalo
                }
                defaultFileLink={get(
                  publicFilesUrls,
                  'prop65_compliance_letter_agreement.read',
                )}
                progressEvent={get(
                  publicFilesUrls,
                  `proposition65.progressEvent`,
                )}
                fileLink={uploadedFileLinks.proposition65}
                exists={!!formState.proposition65}
                onChange={updateFileField}
                fileType={['application/pdf']}
                fullWidth
                required
              />
            </div>

            {!domesticCompany && formState.is_individual && (
              <div className={styles.documentContainer}>
                <div className={styles.row}>
                  <FileInput
                    perms={{
                      canRead: perms.canRead,
                      canUpload: perms.canUpdate && !isMigratedToHalo,
                      canDelete: perms.canDelete && !isMigratedToHalo,
                    }}
                    name="w8ben_form"
                    header="W8BEN FORM"
                    suffix="w8ben_form"
                    downloadText="W8BEN Form Download"
                    desc="Please upload a filled out w8ben form"
                    disabled={
                      isFileFieldDisabled(applicationStatus) ||
                      !perms.canUpdate ||
                      isMigratedToHalo
                    }
                    progressEvent={get(
                      publicFilesUrls,
                      `w8ben_form.progressEvent`,
                    )}
                    defaultFileLink={get(publicFilesUrls, 'w8ben_form.read')}
                    fileLink={uploadedFileLinks.w8ben_form}
                    exists={!!formState.w8ben_form}
                    onChange={updateFileField}
                    fileType={['application/pdf']}
                    fullWidth
                    required
                  />
                </div>
              </div>
            )}

            {!domesticCompany && !formState.is_individual && (
              <div className={styles.documentContainer}>
                <div className={styles.row}>
                  <FileInput
                    perms={{
                      canRead: perms.canRead,
                      canUpload: perms.canUpdate && !isMigratedToHalo,
                      canDelete: perms.canDelete && !isMigratedToHalo,
                    }}
                    name="w8bene_form"
                    header="W8BEN-E FORM"
                    suffix="w8bene_form"
                    downloadText="W8BEN-E Form Download"
                    progressEvent={get(
                      publicFilesUrls,
                      `w8bene_form.progressEvent`,
                    )}
                    desc="Please upload a filled out w8ben-e form"
                    disabled={
                      isFileFieldDisabled(applicationStatus) ||
                      !perms.canUpdate ||
                      isMigratedToHalo
                    }
                    defaultFileLink={get(publicFilesUrls, 'w8bene_form.read')}
                    fileLink={uploadedFileLinks.w8bene_form}
                    exists={!!formState.w8bene_form}
                    onChange={updateFileField}
                    fileType={['application/pdf']}
                    fullWidth
                    required
                  />
                </div>
              </div>
            )}

            <div className={styles.documentContainer}>
              <div className={styles.guidelines}>
                <h4 className={styles.headerRouting}>
                  ADDITIONAL ROUTING AND PRODUCT GUIDELINES
                </h4>
                <p className={styles.uploadLabelDesc}>
                  Please download and follow guidelines for shipping and product
                  details for production.
                </p>
                <div className={styles.uploadButtons}>
                  <Button
                    variant="outlined"
                    id="download-button"
                    color="primary"
                    onClick={showProductGuidelines}
                    small
                    icon
                    flex
                    className={styles.documentButton}
                  >
                    <Download className={styles.iconPadLeft} />
                    Download
                  </Button>
                </div>
              </div>
            </div>

            <div className={styles.documentContainer}>
              <div className={styles.guidelines}>
                <h4 className={styles.headerRouting}>ROUTING INDEX</h4>
                <p className={styles.uploadLabelDesc}>
                  Please download and follow routing index.
                </p>
                <div className={styles.uploadButtons}>
                  <Button
                    variant="outlined"
                    id="download-button"
                    color="primary"
                    onClick={showRoutingIndex}
                    small
                    icon
                    flex
                    className={styles.documentButton}
                  >
                    <Download className={styles.iconPadLeft} />
                    Download
                  </Button>
                </div>
              </div>
            </div>

            <div className={styles.buttonContainer}>
              {perms.canUpdate &&
                applicationStatus === ApplicationStatus.APPROVED &&
                !isMigratedToHalo && (
                  <Button
                    type="submit"
                    className={styles.saveButton}
                    variant="contained"
                    onClick={submitOrSaveVendor}
                    color="primary"
                  >
                    {savedState ? 'Saved!' : 'Save'}
                  </Button>
                )}

              {showSubmitButton(
                internal_id,
                rejection_reason,
                vendorFromNewVendorOnboarding,
              ) &&
                !isMigratedToHalo && (
                  <Button
                    type="submit"
                    className={styles.submit}
                    color="primary"
                    variant="contained"
                    onClick={submitOrSaveVendor}
                    disabled={
                      isFieldDisabled(applicationStatus) || !perms.canUpdate
                    }
                  >
                    {makeSubmitButtonText(applicationStatus)}
                  </Button>
                )}
            </div>

            <div className={styles.complianceContact}>
              {`For any questions related to compliance please contact `}
              <a href="mailto:compliance@fashionnova.com">
                compliance@fashionnova.com
              </a>
            </div>

            {!!size(keys(omitBy(emptyFieldsState, (f) => !f))) && (
              <div className={styles.error} id="rf-error-filled">
                <p>Please make sure to fill out all required fields.</p>
                Missing :
                <ul>
                  {keys(omitBy(emptyFieldsState, (f) => !f)).map((field) => (
                    <li key={field}>{startCase(field)}</li>
                  ))}
                </ul>
              </div>
            )}

            {isCompliance && vendorFromNewVendorOnboarding === false && (
              <ComplianceSection
                currentUser={currentUser}
                vendor={vendor}
                onReject={props.updateVendor}
                user={props.user}
                onApprove={
                  props.vendor.internal_id
                    ? props.updateVendorNetSuite
                    : props.createVendorNetSuite
                }
                onChange={updateInputField}
                onChangeFile={props.uploadApprovalSignatures}
                fileLinks={uploadedFileLinks}
                form={formState}
                perms={perms}
              />
            )}

            {props.vendor.approved_at && (
              <div className={styles.documentContainer}>
                <p>
                  Application{' '}
                  {vendorFromNewVendorOnboarding ? 'submitted' : 'Approved'} on
                  {` ${moment(props.vendor.approved_at).format('LLLL')}`}
                </p>
              </div>
            )}
          </form>
        )}
        {tabState === 1 && (
          <VendorAttributionForm
            vendorAttributes={vendor.attributes || undefined}
            updateVendorAttributes={updateVendorAttributes}
            vendorProfile
            currentUser={currentUser}
            error={vendorAttributesError}
            location={location}
            perms={perms}
          />
        )}
      </Page.Content>
    </Page.Container>
  );
}

VendorApplication.propTypes = {
  updateVendor: PropTypes.func.isRequired,
  updateVendorBanking: PropTypes.func.isRequired,
  createVendorNetSuite: PropTypes.func.isRequired,
  updateVendorNetSuite: PropTypes.func.isRequired,
  vendor: PropTypes.shape({
    internal_id: PropTypes.string,
    applications: PropTypes.array,
    approved_at: PropTypes.string,
  }).isRequired,
  match: matchProps.isRequired,
  location: locationProps.isRequired,
  currentUser: PropTypes.shape({
    role: PropTypes.string,
  }).isRequired,
  publicFilesUrls: PropTypes.shape({}).isRequired,
  updateVendorAttributes: PropTypes.func.isRequired,
  vendorAttributesError: PropTypes.shape({}),
  submitVendor: PropTypes.func.isRequired,
  uploadApprovalSignatures: PropTypes.func.isRequired,
  validateBIC: PropTypes.func.isRequired,
  validateTIN: PropTypes.func.isRequired,
  validateIBAN: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
};

export default VendorApplication;
